import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { cn } from '../utils';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  variant?: 'bordered' | 'ghost' | 'default';
  textareaSize?: 'xs' | 'sm' | 'md' | 'lg';
  state?: 'default' | 'success' | 'warning' | 'error';
  fullWidth?: boolean;
  autoResize?: boolean;
  showCount?: boolean;
  maxLength?: number;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      variant = 'bordered',
      textareaSize = 'md',
      state = 'default',
      fullWidth = true,
      autoResize = false,
      showCount = false,
      maxLength,
      className,
      onChange,
      value,
      ...props
    },
    ref
  ) => {
    const [charCount, setCharCount] = useState(0);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => textareaRef.current!);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (autoResize && textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }

      setCharCount(e.target.value.length);
      onChange?.(e);
    };

    useEffect(() => {
      setCharCount(String(value || '').length);
    }, [value]);

    const textareaClasses = cn(
      'textarea',
      variant === 'bordered' && 'textarea-bordered',
      variant === 'ghost' && 'textarea-ghost',
      textareaSize === 'xs' && 'textarea-xs',
      textareaSize === 'sm' && 'textarea-sm',
      textareaSize === 'lg' && 'textarea-lg',
      state === 'success' && 'textarea-success',
      state === 'warning' && 'textarea-warning',
      state === 'error' && 'textarea-error',
      fullWidth && 'w-full',
      autoResize && 'resize-none overflow-hidden',
      className
    );

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        <textarea
          ref={textareaRef}
          className={textareaClasses}
          onChange={handleChange}
          value={value}
          maxLength={maxLength}
          {...props}
        />
        {showCount && (
          <div className="label">
            <span className="label-text-alt"></span>
            <span className="label-text-alt">
              {charCount}
              {maxLength ? `/${maxLength}` : ''}
            </span>
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
